import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";

import { apiClient } from "../../api";
import keys from "./keys";
import {
  CreateWalletRequest,
  DepositRequest,
  GET_PAYOUT_CONFIG_API_RESPONSE,
  GET_WALLETS_API_RESPONSE,
  WithdrawalParams,
  WithdrawalResponse,
} from "./types";
import { currenciesConfig } from "../../../constants";
import { baseURL } from "../../../utils";

interface GetWalletsReturnType {
  isLoading: boolean;
  data: GET_WALLETS_API_RESPONSE | null;
  isSuccess: boolean;
  refetch: () => void;
  error: unknown;
}

interface GetPayoutConfigReturnType {
  isLoading: boolean;
  data: GET_PAYOUT_CONFIG_API_RESPONSE | null;
  isSuccess: boolean;
  error: unknown;
}

export const payoutConfigResourcePath = "payments/payout/config";
export const walletsResourcePath = "business-wallets/me";

const useGetWallets = (url: string): GetWalletsReturnType => {
  const hash = [keys.read, `${url}`];

  const {
    isLoading,
    data = null,
    isSuccess,
    error,
    refetch,
  }: UseQueryResult<GET_WALLETS_API_RESPONSE | null, unknown> = useQuery(
    hash,
    async () => await apiClient.get({ url }),
    {
      refetchOnWindowFocus: false,
    },
  );
  return { isLoading, data, isSuccess, error, refetch };
};

const useGetPayoutConfig = (
  amount: string,
  currency: string,
  currencyPaymentMethod?: string,
): GetPayoutConfigReturnType => {
  let paymentMethod = currencyPaymentMethod;
  if (!paymentMethod) {
    // get default payment method for currency
    paymentMethod = currenciesConfig.find(
      (detail) => detail.currency === currency,
    )?.paymentMethods[0]?.value;
  }

  const url =
    Number(amount) && currency
      ? `${baseURL}/${payoutConfigResourcePath}?amount=${amount}&currency=${currency}&paymentMethod=${paymentMethod}`
      : "";
  const hash = [keys.read, url];

  const {
    isInitialLoading: isLoading,
    data = null,
    isSuccess,
    error,
  }: UseQueryResult<GET_PAYOUT_CONFIG_API_RESPONSE | null, unknown> = useQuery(
    hash,
    async () => await apiClient.get({ url }),
    {
      enabled: !!url,
    },
  );
  return { isLoading, data, isSuccess, error };
};

const useCreateWithdrawal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      data: body,
      walletId,
    }: WithdrawalParams): Promise<WithdrawalResponse> => {
      const response = await apiClient.post({
        url: `business-wallets/${walletId}/withdraw`,
        body,
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the wallets query to refresh balances
      queryClient.invalidateQueries({
        queryKey: [keys.read],
      });
    },
  });
};

const useCreateWallet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreateWalletRequest) => {
      const response = await apiClient.post({
        url: walletsResourcePath,
        body,
      });
      return response.data;
    },
    onSuccess: () => {
      // Refresh the wallets list so the newly created wallet appears
      queryClient.invalidateQueries({
        queryKey: [keys.read],
      });
    },
  });
};

const useWalletDeposit = () => {
  return useMutation({
    mutationFn: async ({
      walletId,
      request,
    }: {
      walletId: string;
      request: DepositRequest;
    }) => {
      const response = await apiClient.post({
        url: `business-wallets/${walletId}/deposit`,
        body: request,
      });
      return response.data;
    },
  });
};

function clearWalletsQueryCache(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [keys.read],
  });
}

export {
  useGetWallets,
  useGetPayoutConfig,
  useCreateWithdrawal,
  useCreateWallet,
  useWalletDeposit,
  clearWalletsQueryCache,
};
